import { PENDING_INTENT_STORAGE_KEY } from './jobMatchStorage';

export { PENDING_INTENT_STORAGE_KEY };

export const PENDING_INTENT_VERSION = 1;
export const PENDING_INTENT_TTL_MS = 30 * 60 * 1000;
export const AUTH_FALLBACK_PATH = '/dashboard';

function storage() {
  if (typeof window === 'undefined') return null;
  try { return window.sessionStorage; } catch (_) { return null; }
}

export function isSafeInternalPath(path) {
  if (typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.includes('\\')) return false;
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://jobmatchpro.local';
    const parsed = new URL(trimmed, origin);
    if (parsed.origin !== origin) return false;
    if (parsed.pathname === '/sign-in') return false;
    return `${parsed.pathname}${parsed.search}${parsed.hash}` === trimmed;
  } catch (_) {
    return false;
  }
}

export function normalizeReturnPath(path, fallback = AUTH_FALLBACK_PATH) {
  return isSafeInternalPath(path) ? path : fallback;
}

export function createPendingIntent({
  kind = 'auth_return',
  return_path,
  job_id = null,
  job_title = null,
  company = null,
  ttlMs = PENDING_INTENT_TTL_MS,
} = {}) {
  const safeReturnPath = normalizeReturnPath(return_path, AUTH_FALLBACK_PATH);
  const now = Date.now();
  const intent = {
    version: PENDING_INTENT_VERSION,
    kind,
    return_path: safeReturnPath,
    job_id: job_id || null,
    job_title: job_title || null,
    company: company || null,
    created_at: new Date(now).toISOString(),
    expires_at: new Date(now + ttlMs).toISOString(),
  };
  try { storage()?.setItem(PENDING_INTENT_STORAGE_KEY, JSON.stringify(intent)); } catch (_) { /* no-op */ }
  return intent;
}

export function readPendingIntent() {
  try {
    const raw = storage()?.getItem(PENDING_INTENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.version !== PENDING_INTENT_VERSION) return null;
    if (!isSafeInternalPath(parsed.return_path)) return null;
    if (!parsed.expires_at || Date.parse(parsed.expires_at) <= Date.now()) {
      clearPendingIntent();
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearPendingIntent() {
  try { storage()?.removeItem(PENDING_INTENT_STORAGE_KEY); } catch (_) { /* no-op */ }
}

export function consumePendingIntent() {
  const intent = readPendingIntent();
  if (intent) clearPendingIntent();
  return intent;
}

export function resolveAuthDestination({ attemptedReturnPath, consume = false } = {}) {
  const pending = consume ? consumePendingIntent() : readPendingIntent();
  if (pending?.return_path && isSafeInternalPath(pending.return_path)) return pending.return_path;
  return normalizeReturnPath(attemptedReturnPath, AUTH_FALLBACK_PATH);
}

export function authCallbackUrl() {
  if (typeof window === 'undefined') return '/sign-in';
  return `${window.location.origin}/sign-in`;
}

export function currentInternalPath() {
  if (typeof window === 'undefined') return AUTH_FALLBACK_PATH;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
